/** Temporary chats live only in this browser (IndexedDB), never on the server unless saved. */
import { createStore, del, delMany, entries, get, set } from "idb-keyval";
import type { MessageStatus, Part, Usage } from "./types";

export interface TempMessage {
  id: string;
  role: "user" | "assistant";
  parts: Part[];
  status: MessageStatus;
  error?: string | null;
  model_name?: string;
  usage?: Usage;
}

export interface TempChat {
  id: string;
  title: string;
  model_id: string | null;
  /** MCP servers switched on; null = the default ones. */
  mcp_servers?: string[] | null;
  folder_id?: string | null;
  /** From a template; null = the user's default prompt. */
  system_prompt?: string | null;
  /** Private: files (uploads and what tools make) are kept in this browser too, not on the server. */
  private?: boolean;
  messages: TempMessage[];
  updated_at: string;
}

const store = createStore("webthing-temp", "chats");

export const tempChats = {
  get: (id: string) => get<TempChat>(id, store),
  put: (chat: TempChat) => set(chat.id, { ...chat, updated_at: new Date().toISOString() }, store),
  delete: (id: string) => del(id, store),
  list: async () =>
    (await entries<string, TempChat>(store)).map(([, c]) => c).sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
};

/** Files of private temporary chats; ids start with "local:". */
export interface LocalFile {
  id: string;
  name: string;
  mime: string;
  blob: Blob;
}

const fileStore = createStore("webthing-temp-files", "files");

export const LOCAL = "local:";
export const isLocal = (id: string | undefined | null) => !!id && id.startsWith(LOCAL);

export const localFiles = {
  get: (id: string) => get<LocalFile>(id, fileStore),
  put: (f: LocalFile) => set(f.id, f, fileStore),
  deleteMany: (ids: string[]) => delMany(ids, fileStore),
};

/** Local file ids used by messages (uploads and files tools made). */
export function localIds(messages: { parts: Part[] }[]): string[] {
  const ids = new Set<string>();
  for (const m of messages)
    for (const p of m.parts) {
      if (p.type === "attachment" && isLocal(p.id)) ids.add(p.id);
      if (p.type === "tool_call") for (const r of p.result ?? []) if (r.type === "attachment" && isLocal(r.id)) ids.add(r.id);
    }
  return [...ids];
}

/** Message parts with attachment ids replaced (e.g. local files after uploading them). */
export function replaceIds(parts: Part[], ids: Record<string, string>): Part[] {
  return parts.map((p) => {
    if (p.type === "attachment") return { ...p, id: ids[p.id] ?? p.id };
    if (p.type === "tool_call" && p.result) return { ...p, result: replaceIds(p.result, ids) };
    return p;
  });
}

/** The server's copy of a private file (it keeps them in memory for a while). */
export function serverLocalUrl(id: string, download = false) {
  return `/api/files/local/${id.slice(LOCAL.length)}${download ? "?download=true" : ""}`;
}

/** Gets a private file the browser does not have yet from the server's memory. */
export async function fetchLocalFile(id: string, name: string, mime: string): Promise<LocalFile | null> {
  const have = await localFiles.get(id);
  if (have) return have;
  const r = await fetch(serverLocalUrl(id));
  if (!r.ok) return null;
  const f = { id, name, mime, blob: await r.blob() };
  await localFiles.put(f);
  return f;
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function base64ToBlob(data: string, mime: string): Blob {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export function newId(): string {
  // crypto.randomUUID only exists in secure contexts (https or localhost).
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
